import * as ResizablePrimitive from "react-resizable-panels";
import DotsGridIcon from "@src/lib/icons/12px/dots-grid";

import { cn } from "@src/lib/utils/cn";

const ResizablePanelGroup = ({
  className,
  ...props
}: React.ComponentProps<
  typeof ResizablePrimitive.PanelGroup
>): React.JSX.Element => (
  <ResizablePrimitive.PanelGroup
    className={cn(
      "flex h-full w-full data-[panel-group-direction=vertical]:flex-col",
      className,
    )}
    {...props}
  />
);

const ResizablePanel = ResizablePrimitive.Panel;

// 6px each side for a mouse (13px with the line) so the 12px grip is
// grabbable edge to edge; 15px for touch, the library default.
const HIT_AREA_MARGINS = { coarse: 15, fine: 6 };

/**
 * A 1px divider between panels. react-resizable-panels hit-tests the line
 * itself plus hitAreaMargins (6px each side for a mouse here, 15px for touch)
 * and sets data-resize-handle-state to hover / drag, so it darkens on hover
 * and turns brand on drag. `withHandle` adds a 12px grip pill in the middle,
 * inside that hit area.
 */
const ResizableHandle = ({
  withHandle,
  className,
  hitAreaMargins = HIT_AREA_MARGINS,
  ...props
}: React.ComponentProps<typeof ResizablePrimitive.PanelResizeHandle> & {
  withHandle?: boolean;
}): React.JSX.Element => (
  <ResizablePrimitive.PanelResizeHandle
    hitAreaMargins={hitAreaMargins}
    className={cn(
      "relative flex w-px items-center justify-center bg-border-primary transition-colors",
      "data-[resize-handle-state=hover]:bg-border-tertiary data-[resize-handle-state=drag]:bg-action-primary",
      "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-border",
      "data-[panel-group-direction=vertical]:h-px data-[panel-group-direction=vertical]:w-full [&[data-panel-group-direction=vertical]>div]:rotate-90",
      className,
    )}
    {...props}
  >
    {withHandle && (
      <div className="z-10 flex h-6 w-3 items-center justify-center rounded-full border border-border-primary bg-bg-primary text-icon-tertiary shadow-default">
        <DotsGridIcon className="size-2.5" />
      </div>
    )}
  </ResizablePrimitive.PanelResizeHandle>
);

export { ResizablePanelGroup, ResizablePanel, ResizableHandle };
